import { createContext, useContext, useMemo } from 'react';
import { fromJS } from 'immutable';
import { COMMERCE_PAYMENT_TYPE_ID } from 'customer-data-objects/constants/ObjectTypeIds';
import { useSendCrmMessageTopic } from 'crm-message-bus/useSendCrmMessageTopic';
import { TOPIC_NAMES } from 'crm-message-bus/types/MessageTopics';
import { useCallback } from 'react';
/**
 * Taken from customer-data-sidebar
 */

/**
 * Context for callbacks that handle opening the communicator, making updates after object creates,
 * and anything else the host app might care about from changes made within the sidebar
 *
 * @param {object}   value                     the context value
 *
 * @callback onObjectUpdate                    callback that fires when object properties or associations change
 * @param {String} objectType                  type of the object which was updated
 * @param {String} objectId                    ID of the object which was updated
 * @param {Immutable.Map} properties                     optional Map of updated property names to values
 */
import { jsx as _jsx } from "react/jsx-runtime";
const HostAppCallbacksContext = /*#__PURE__*/createContext({});

/**
 * This context is used to pass object updates up to the host app.
 * Ideally, this would be done via a shared apollo client, but for now this is the
 * best we can do until a unified client is created and put in place.
 */
export const HostAppCallbacksContextProvider = ({
  onObjectUpdate,
  onObjectDelete,
  children,
  objectId,
  closeSidebar
}) => {
  const sendCrmPaymentUpdatedMessage = useSendCrmMessageTopic(TOPIC_NAMES.UPDATE_OBJECT);
  const updatePaymentsSidebarCard = useCallback(() => sendCrmPaymentUpdatedMessage({
    objectTypeId: COMMERCE_PAYMENT_TYPE_ID,
    objectId
  }), [objectId, sendCrmPaymentUpdatedMessage]);
  const context = useMemo(() => ({
    updateAppCRMObject: properties => {
      if (!properties) {
        return;
      }
      if (typeof onObjectUpdate !== 'function') {
        /** There are at least two cases here:
         * 1. We are not on the index page
         * 2. There's no onObjectUpdate callback for some other reason
         * In either case, we don't want to do anything.
         */
        return;
      }
      const payload = collateHostAppCallbackProperties(properties);
      if (Object.keys(payload).length === 0) {
        return;
      }
      onObjectUpdate({
        objectType: COMMERCE_PAYMENT_TYPE_ID,
        objectId,
        properties: fromJS(payload)
      });
      updatePaymentsSidebarCard();
    },
    deleteAppCRMObject: ({
      updatedInvoiceId,
      updatedInvoiceProperties
    }) => {
      if (typeof onObjectDelete !== 'function' || typeof onObjectUpdate !== 'function') {
        return;
      }
      // These are both needed as onObjectDelete makes optimistic updates to the index page and updatePaymentsSidebarCard sends a message so that the record page sidebar card refetches information
      onObjectDelete({
        objectTypeId: COMMERCE_PAYMENT_TYPE_ID,
        objectId
      });
      updatePaymentsSidebarCard();
      if (updatedInvoiceId && updatedInvoiceProperties) {
        const payload = Object.entries(updatedInvoiceProperties).reduce((acc, [propertyName, {
          value
        }]) => {
          if (typeof value === 'string') {
            acc[propertyName] = value;
          }
          return acc;
        }, {});
        onObjectUpdate({
          objectType: '0-53',
          objectId: updatedInvoiceId,
          properties: fromJS(payload)
        });
      }
    },
    closeSidebar
  }), [onObjectUpdate, onObjectDelete, objectId, closeSidebar, updatePaymentsSidebarCard]);
  return /*#__PURE__*/_jsx(HostAppCallbacksContext.Provider, {
    value: context,
    children: children
  });
};

// These property names determine what properties are passed up to the host app,
// and what properties are ignored
const updatableProperties = ['hs_refunds_amount', 'hs_net_amount', 'hs_latest_status', 'hs_latest_status_updated_date', 'hs_lastmodifieddate', 'hs_updated_by_user_id'];
const isValidUpdatePropertyName = propertyName => updatableProperties.includes(propertyName);
const isValidUpdatePropertyValue = propertyValue => typeof propertyValue === 'string' && propertyValue.length > 0;
export function collateHostAppCallbackProperties(properties) {
  return Object.entries(properties).reduce((acc, [propertyName, {
    value
  }]) => {
    if (isValidUpdatePropertyValue(value) && isValidUpdatePropertyName(propertyName)) {
      acc[propertyName] = value;
    }
    return acc;
  }, {});
}
export const useHostAppCallbacksContext = () => useContext(HostAppCallbacksContext);